import { useState, useCallback, useRef, useEffect } from 'react';
import { pdfGenerationService } from '../services/pdfGenerationService';
import { useSnackbar } from 'notistack';

interface PdfJob {
  jobId: string;
  fileName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  downloadUrl?: string;
  error?: string;
  metadata?: {
    batchId: number;
    batchNumber: string;
    farmer: string;
    farm: string;
    selectedPlot: string;
    generatedAt: string;
  };
  startTime: Date;
  pollCount: number;
}

export const usePdfStatusManager = () => {
  const [jobs, setJobs] = useState<PdfJob[]>([]);
  const pollingRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const { enqueueSnackbar } = useSnackbar();

  // Cleanup polling intervals on unmount
  useEffect(() => {
    const currentPollingRefs = pollingRefs.current;
    return () => {
      currentPollingRefs.forEach(timeout => clearTimeout(timeout));
      currentPollingRefs.clear();
    };
  }, []);

  const updateJobStatus = useCallback((
    jobId: string,
    updates: Partial<Pick<PdfJob, 'status' | 'progress' | 'downloadUrl' | 'error'>>
  ) => {
    setJobs(prev => prev.map(job =>
      job.jobId === jobId
        ? { ...job, ...updates }
        : job
    ));
  }, []);

  const scheduleNextPoll = useCallback((jobId: string, pollJobStatusFn: (jobId: string) => Promise<void>) => {
    const timeout = setTimeout(() => {
      pollJobStatusFn(jobId);
    }, 10000); // Poll every 10 seconds

    pollingRefs.current.set(jobId, timeout);
  }, []);

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const job = jobs.find(j => j.jobId === jobId);
      if (!job) return;

      // Increment poll count
      setJobs(prev => prev.map(j =>
        j.jobId === jobId
          ? { ...j, pollCount: j.pollCount + 1 }
          : j
      ));

      const status = await pdfGenerationService.getPdfJobStatus(jobId);

      switch (status.status) {
        case 'completed': {
          updateJobStatus(jobId, {
            status: 'completed',
            downloadUrl: status.downloadUrl,
            progress: 100
          });

          // Stop polling
          const timeout = pollingRefs.current.get(jobId);
          if (timeout) {
            clearTimeout(timeout);
            pollingRefs.current.delete(jobId);
          }

          enqueueSnackbar(`PDF generated successfully: ${job.fileName}`, {
            variant: 'success',
            autoHideDuration: 5000
          });

          // Auto-download if available
          if (status.downloadUrl) {
            setTimeout(() => {
              const link = document.createElement('a');
              link.href = status.downloadUrl!;
              link.download = status.fileName || job.fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }, 1000);
          }
          break;
        }

        case 'failed': {
          updateJobStatus(jobId, {
            status: 'failed',
            error: status.error || 'PDF generation failed'
          });

          // Stop polling
          const failTimeout = pollingRefs.current.get(jobId);
          if (failTimeout) {
            clearTimeout(failTimeout);
            pollingRefs.current.delete(jobId);
          }

          enqueueSnackbar(`PDF generation failed: ${status.error || 'Unknown error'}`, {
            variant: 'error',
            persist: true
          });
          break;
        }

        case 'processing':
          updateJobStatus(jobId, {
            status: 'processing',
            progress: status.progress
          });

          // Continue polling if under max attempts
          if (job.pollCount < 60) { // 10 minutes max (10-second intervals)
            // eslint-disable-next-line react-hooks/immutability -- self-referential polling callback; only invoked asynchronously after declaration completes
            scheduleNextPoll(jobId, pollJobStatus);
          } else {
            updateJobStatus(jobId, {
              status: 'failed',
              error: 'PDF generation timeout'
            });

            const timeoutRef = pollingRefs.current.get(jobId);
            if (timeoutRef) {
              clearTimeout(timeoutRef);
              pollingRefs.current.delete(jobId);
            }

            enqueueSnackbar('PDF generation timed out. Please try again.', {
              variant: 'warning',
              persist: true
            });
          }
          break;

        default:
          // Continue polling for pending status
          if (job.pollCount < 60) {
            scheduleNextPoll(jobId, pollJobStatus);
          } else {
            updateJobStatus(jobId, {
              status: 'failed',
              error: 'PDF generation timeout'
            });

            const timeoutRef = pollingRefs.current.get(jobId);
            if (timeoutRef) {
              clearTimeout(timeoutRef);
              pollingRefs.current.delete(jobId);
            }
          }
      }
    } catch {

      const job = jobs.find(j => j.jobId === jobId);
      if (job && job.pollCount < 60) {
        // Continue polling on error, but limit attempts
        scheduleNextPoll(jobId, pollJobStatus);
      } else {
        updateJobStatus(jobId, {
          status: 'failed',
          error: 'Network error while checking status'
        });

        const timeoutRef = pollingRefs.current.get(jobId);
        if (timeoutRef) {
          clearTimeout(timeoutRef);
          pollingRefs.current.delete(jobId);
        }
      }
    }
  }, [jobs, updateJobStatus, enqueueSnackbar, scheduleNextPoll]);

  const startPolling = useCallback((jobId: string) => {
    // Initial poll after 2 seconds
    const initialTimeout = setTimeout(() => {
      pollJobStatus(jobId);
    }, 2000);

    pollingRefs.current.set(jobId, initialTimeout);
  }, [pollJobStatus]);

  const addJob = useCallback((
    jobId: string,
    fileName: string,
    metadata?: PdfJob['metadata']
  ) => {
    const newJob: PdfJob = {
      jobId,
      fileName,
      status: 'pending',
      metadata,
      startTime: new Date(),
      pollCount: 0
    };

    setJobs(prev => [...prev, newJob]);
    startPolling(jobId);

    enqueueSnackbar(`PDF generation started: ${fileName}`, {
      variant: 'info',
      autoHideDuration: 3000
    });
  }, [enqueueSnackbar, startPolling]);

  const removeJob = useCallback((jobId: string) => {
    // Stop polling
    const timeout = pollingRefs.current.get(jobId);
    if (timeout) {
      clearTimeout(timeout);
      pollingRefs.current.delete(jobId);
    }

    // Remove from jobs list
    setJobs(prev => prev.filter(job => job.jobId !== jobId));
  }, []);

  const retryJob = useCallback(async (jobId: string) => {
    const job = jobs.find(j => j.jobId === jobId);
    if (!job) return;

    try {
      // Reset job status and restart polling
      updateJobStatus(jobId, {
        status: 'pending',
        error: undefined,
        progress: undefined
      });

      setJobs(prev => prev.map(j =>
        j.jobId === jobId
          ? { ...j, startTime: new Date(), pollCount: 0 }
          : j
      ));

      startPolling(jobId);
      enqueueSnackbar(`Retrying PDF generation: ${job.fileName}`, {
        variant: 'info',
        autoHideDuration: 3000
      });
    } catch {
      enqueueSnackbar('Failed to retry PDF generation', { variant: 'error' });
    }
  }, [jobs, updateJobStatus, enqueueSnackbar, startPolling]);


  const downloadJob = useCallback((jobId: string) => {
    const job = jobs.find(j => j.jobId === jobId);
    if (job && job.downloadUrl) {
      const link = document.createElement('a');
      link.href = job.downloadUrl;
      link.download = job.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(`Downloaded: ${job.fileName}`, {
        variant: 'success',
        autoHideDuration: 3000
      });
    }
  }, [jobs, enqueueSnackbar]);

  const getActiveJobs = useCallback(() => {
    return jobs.filter(job => job.status === 'pending' || job.status === 'processing');
  }, [jobs]);

  const getCompletedJobs = useCallback(() => {
    return jobs.filter(job => job.status === 'completed');
  }, [jobs]);

  const getFailedJobs = useCallback(() => {
    return jobs.filter(job => job.status === 'failed');
  }, [jobs]);

  return {
    jobs,
    addJob,
    removeJob,
    retryJob,
    downloadJob,
    getActiveJobs,
    getCompletedJobs,
    getFailedJobs,
    updateJobStatus
  };
};